import React from "react";
import { Link } from "react-router-dom";
import { Printer, Lock, Loader2 } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Logo from "../components/Logo";
import { CONTACT } from "../data/build";
import { api, getToken } from "../software/api";

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/shop/interior", label: "Interior" },
  { to: "/shop/brief", label: "Brief / RFQ" },
  { to: "/shop/ip", label: "IP & Prior-Art" },
  { to: "/software", label: "Software" },
];

const REV = "Rev A · concept stage";
const DOC_REF = "IP-ANX-001";

const Label = ({ children }) => (
  <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-sky-700">{children}</p>
);
const H = ({ n, children }) => (
  <div className="flex items-baseline gap-3 border-b border-slate-300 pb-2 mb-4">
    <span className="font-mono2 text-sm font-bold text-sky-600">{n}</span>
    <h2 className="font-display text-2xl font-bold text-slate-900">{children}</h2>
  </div>
);
const Page = ({ children }) => (
  <section className="brief-page bg-white rounded-xl border border-slate-200 shadow-sm px-8 py-9 md:px-12 md:py-12 mb-6">
    {children}
  </section>
);
const Foot = ({ p }) => (
  <div className="mt-10 pt-3 border-t border-slate-200 flex justify-between font-mono2 text-[9px] uppercase tracking-widest text-slate-400">
    <span>Aqualux Nexus · IP Strategy &amp; Prior-Art Brief · Confidential</span>
    <span>{REV} · {DOC_REF} · Page {p}</span>
  </div>
);

// IP protection tools
const TOOLS = [
  ["Utility patent", "How the device works — a specific novel mechanism/method", "Reagentless hybrid measurement, self-clean & auto-cal sequence, guided-strip architecture", "Slow, costly, publishes your method"],
  ["Registered design", "How the device looks — its distinctive appearance", "The finished enclosure / form factor", "Fast, cheap, high certainty"],
  ["Trademark", "The brand name & logo", "\u201CAqualux Nexus\u201D + logo", "Cheapest, fastest — do first"],
  ["Trade secret / copyright", "Software chemistry logic, dosing algorithms, source code", "Dosing recommendation engine, client-profile logic", "No publication; protects the \u201Csecret sauce\u201D"],
];

// Competitive prior-art landscape (doubles as investor comparison)
const PRIOR_ART = [
  { name: "LaMotte WaterLink Spin", method: "Centrifugal reagent SpinDisk photometer (6-wavelength)", consumable: "Yes — sealed reagent disks", params: "~14 incl. phosphate, salt, hardness, CYA, copper", note: "Pool-shop benchtop incumbent worldwide" },
  { name: "WaterGuru SENSE", method: "Optical reflectance reading of DPD sensing pads (patented)", consumable: "Yes — sensing cassettes (1–2 mo)", params: "FC, pH, TA, CYA, CH", note: "Skimmer-mounted; freedom-to-operate risk" },
  { name: "Sutro", method: "Robotic liquid-reagent colorimetric flow cell", consumable: "Yes — reagent cartridges (monthly)", params: "FC, pH, TA", note: "In-pool monitor + subscription" },
  { name: "pHin (discontinued)", method: "ORP electrical potential", consumable: "No", params: "Sanitiser estimate only", note: "Obsolete — drift/inaccuracy, unsupported" },
];

// Assessment of the 9 proposed claim ideas
const CLAIMS = [
  { id: "1–4", idea: "2/3-strip optical tester ± conductivity probe", verdict: "weak", text: "One invention, not four (embodiments). Core concept overlaps WaterGuru (optical pads) + LaMotte (photometer+strips). Not novel as a category." },
  { id: "5", idea: "Water-injected test device", verdict: "weak", text: "Standard fill / flow-through sampling — established prior art." },
  { id: "6", idea: "Water-draining test device", verdict: "weak", text: "Established drain / flush sampling — prior art." },
  { id: "7", idea: "Self-cleaning test device", verdict: "narrow", text: "Concept is patented prior art (flush, pump-reversal, wipers, acoustic). A specific flood-&-overflow + fresh-water auto-calibration mechanism may support a narrow claim." },
  { id: "8", idea: "10-in-1 incl. phosphate + magnesium + algae", verdict: "narrow", text: "A bundle of known tests usually fails as obvious. Magnesium (mineral pools) + reagentless fluorescence algae are less common, but \u201Cmore tests in one box\u201D isn\u2019t inventive by itself." },
  { id: "9", idea: "Algae detection", verdict: "weak", text: "Not novel — fluorescence/chlorophyll algae detection already patented in pool context." },
];

// The genuine defensible novelty
const NOVELTY = [
  ["Reagentless / zero-consumables", "Sutro, LaMotte & WaterGuru all rely on consumables (cartridges/disks/pads with recurring cost). A full panel with no liquid reagents and no consumable pads is both the strongest novelty and the strongest market story."],
  ["Self-clean + fresh-water auto-calibration", "The specific flood/overflow gutter sequence that self-cleans and re-calibrates on fresh water — a narrow, mechanism-level claim."],
  ["Guided 3-slot strip system", "Strip-present sensors + auto-detection + prompt-LEDs, driven by the client\u2019s stored pool profile — a specific hardware + workflow combination."],
  ["Integrated system claim", "Reagentless probes + optical strips + fluorescence + self-clean orchestrated as one apparatus/method feeding a central database."],
];

// Recommended actions (cheapest/most certain first)
const ACTIONS = [
  ["1", "Trademark \u201CAqualux Nexus\u201D + logo", "Cheap, fast, high-certainty. Do this first."],
  ["2", "Registered design on the enclosure", "Protects the distinctive look — quick and relatively cheap."],
  ["3", "Professional prior-art / freedom-to-operate search", "Tests novelty AND checks you don\u2019t infringe WaterGuru / LaMotte / Sutro. Do before any utility filing."],
  ["4", "Utility patent via a registered attorney", "Claims focused narrowly on the reagentless mechanism + self-clean/auto-cal + guided-strip architecture — not broad categories."],
  ["5", "Keep chemistry logic as trade secret / copyright", "Dosing algorithms are often better protected unpublished than patented."],
];

const VERDICT_BADGE = {
  weak: { label: "Low novelty", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  narrow: { label: "Possible — narrow", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  strong: { label: "Defensible", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

export default function ShopIP() {
  const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  const [access, setAccess] = React.useState("loading"); // loading | admin | denied

  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!getToken()) { setAccess("denied"); return; }
      try {
        const me = await api("GET", "/auth/me");
        if (active) setAccess(me.role === "store_admin" ? "admin" : "denied");
      } catch (e) { if (active) setAccess("denied"); }
    })();
    return () => { active = false; };
  }, []);

  if (access !== "admin") {
    return (
      <div className="min-h-screen bg-slate-100" data-testid="shop-ip-page">
        <div className="no-print"><TopBar links={LINKS} /></div>
        <div className="mx-auto max-w-md px-5 py-24 text-center" data-testid="ip-locked">
          {access === "loading" ? (
            <p className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking access…</p>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white"><Lock className="h-5 w-5" /></span>
              <h1 className="font-display text-xl font-bold text-slate-900">Admin only</h1>
              <p className="mt-2 text-sm text-slate-600">The IP Strategy &amp; Prior-Art Brief is restricted to the site owner. Please sign in as admin to view it.</p>
              <Link to="/software" data-testid="ip-admin-login-link" className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors"><Lock className="h-4 w-4" /> Admin login</Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="shop-ip-page">
      <div className="no-print"><TopBar links={LINKS} /></div>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 bg-slate-900 text-white">
        <div className="mx-auto max-w-[900px] px-5 h-14 flex items-center justify-between">
          <span className="font-mono2 text-xs uppercase tracking-widest text-slate-300 hidden sm:inline">IP Strategy &amp; Prior-Art Brief · print or save as PDF</span>
          <div className="flex items-center gap-2">
            <Link to="/shop/brief" data-testid="ip-to-brief-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/10 text-white text-sm font-semibold px-4 py-2 transition-colors">Manufacturing Brief</Link>
            <button onClick={() => window.print()} data-testid="ip-print-btn" className="inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold px-5 py-2 transition-colors">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="brief-doc mx-auto max-w-[900px] px-4 md:px-6 py-8">

        {/* PAGE 1 — COVER + STRATEGY */}
        <Page>
          <div className="flex items-center justify-between">
            <Logo />
            <span className="font-mono2 text-[10px] text-slate-400">{REV}</span>
          </div>
          <div className="mt-3"><Label>Confidential · prepared for patent-attorney intake &amp; investor review</Label></div>

          <div className="mt-10">
            <p className="font-mono2 text-sm text-sky-600 uppercase tracking-widest">Intellectual Property Strategy &amp; Prior-Art Brief</p>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Aqualux Nexus<br />Pool-Water Testing Platform
            </h1>
            <div className="mt-5 grid sm:grid-cols-2 gap-x-10 gap-y-1 text-sm">
              <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100"><span className="text-slate-500">Applicant</span><span className="text-slate-900 font-medium">{CONTACT.name}</span></div>
              <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100"><span className="text-slate-500">Entity</span><span className="text-slate-900 font-medium">{CONTACT.company}</span></div>
              <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100"><span className="text-slate-500">Location</span><span className="text-slate-900 font-medium">{CONTACT.location}</span></div>
              <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100"><span className="text-slate-500">Contact</span><span className="text-slate-900 font-medium">{CONTACT.email}</span></div>
              <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100"><span className="text-slate-500">Doc ref</span><span className="text-slate-900 font-medium">{DOC_REF}</span></div>
              <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100"><span className="text-slate-500">Date</span><span className="text-slate-900 font-medium">{today}</span></div>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border-l-4 border-amber-400 bg-amber-50/70 p-5" data-testid="ip-disclaimer">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-amber-700">Important — not legal advice</p>
            <p className="mt-2 text-slate-700 leading-relaxed text-sm">
              This document is a research-informed strategy summary to support an intake conversation with a registered patent
              attorney. It is <strong>not legal advice</strong> and is <strong>not a professional patentability or freedom-to-operate opinion</strong>.
              Novelty and infringement must be confirmed by a qualified attorney and a professional prior-art search before any filing.
            </p>
          </div>

          <div className="mt-8">
            <Label>Core principle</Label>
            <p className="mt-2 text-slate-700 leading-relaxed">
              Patents protect a <strong>specific novel mechanism or method</strong> (the claims) — not a product category or a feature list.
              &ldquo;A 3-strip water tester&rdquo; is not patentable; &ldquo;a device that achieves X via this specific novel arrangement Y&rdquo; may be.
              Four protection tools apply, and the smart strategy uses several in parallel:
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b border-slate-300 text-slate-500 font-mono2 text-[10px] uppercase tracking-wider">
                    <th className="py-2 pr-3">Tool</th><th className="py-2 pr-3">Protects</th><th className="py-2 pr-3">For Aqualux</th><th className="py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {TOOLS.map(([tool, protects, forus, notes]) => (
                    <tr key={tool} className="border-b border-slate-100 align-top">
                      <td className="py-2 pr-3 font-semibold text-slate-900">{tool}</td>
                      <td className="py-2 pr-3 text-slate-600">{protects}</td>
                      <td className="py-2 pr-3 text-slate-600">{forus}</td>
                      <td className="py-2 text-slate-500">{notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Foot p="1" />
        </Page>

        {/* PAGE 2 — PRIOR-ART LANDSCAPE (investor comparison) */}
        <Page>
          <H n="1">Competitive prior-art landscape</H>
          <p className="text-slate-600 text-sm mb-4">
            The devices below are the prior art any Aqualux patent must be novel over — and the field it must out-compete.
            The common thread: every established smart tester relies on <strong>consumables</strong>. Aqualux&rsquo;s reagentless,
            zero-consumables approach is the clearest point of difference.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b border-slate-300 text-slate-500 font-mono2 text-[10px] uppercase tracking-wider">
                  <th className="py-2 pr-3">Device</th><th className="py-2 pr-3">Method</th><th className="py-2 pr-3">Consumables</th><th className="py-2 pr-3">Parameters</th><th className="py-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {PRIOR_ART.map((d) => (
                  <tr key={d.name} className="border-b border-slate-100 align-top">
                    <td className="py-2 pr-3 font-semibold text-slate-900">{d.name}</td>
                    <td className="py-2 pr-3 text-slate-600">{d.method}</td>
                    <td className="py-2 pr-3 text-slate-600">{d.consumable}</td>
                    <td className="py-2 pr-3 text-slate-600">{d.params}</td>
                    <td className="py-2 text-slate-500">{d.note}</td>
                  </tr>
                ))}
                <tr className="border-b-2 border-sky-300 align-top bg-sky-50/60">
                  <td className="py-2.5 pr-3 font-bold text-sky-800">Aqualux Nexus <span className="font-normal text-sky-600">(proposed)</span></td>
                  <td className="py-2.5 pr-3 text-slate-700">4 continuous probes (FC · pH · salt/TDS · temp) + 3 optical strips + fluorescence algae</td>
                  <td className="py-2.5 pr-3 font-semibold text-emerald-700">None — no liquid reagents</td>
                  <td className="py-2.5 pr-3 text-slate-700">FC, pH, salt, temp, 6-in-1, phosphate, magnesium, algae</td>
                  <td className="py-2.5 text-slate-600">Self-cleaning; shop + mobile + home + software</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-[11px] text-slate-400">
            Sources: manufacturer literature (LaMotte, WaterGuru, Sutro) and published pool water-analysis patents. Indicative — verify with a professional search.
          </p>
          <Foot p="2" />
        </Page>

        {/* PAGE 3 — ASSESSMENT OF PROPOSED CLAIMS */}
        <Page>
          <H n="2">Assessment of the proposed claim ideas</H>
          <p className="text-slate-600 text-sm mb-4">
            An honest first-pass read of the nine ideas against the prior art above. Most are weak <em>as categories</em>;
            value concentrates in a few specific mechanisms (Section 3).
          </p>
          <div className="space-y-3">
            {CLAIMS.map((c) => {
              const b = VERDICT_BADGE[c.verdict];
              return (
                <div key={c.id} className="rounded-xl border border-slate-200 p-4" data-testid={`ip-claim-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono2 text-xs font-bold text-sky-600">{c.id}</span>
                      <span className="font-semibold text-slate-900">{c.idea}</span>
                    </div>
                    <span className={`shrink-0 inline-block rounded-full border text-[10px] font-semibold px-2.5 py-0.5 ${b.cls}`}>{b.label}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
          <Foot p="3" />
        </Page>

        {/* PAGE 4 — DEFENSIBLE NOVELTY + ACTIONS */}
        <Page>
          <H n="3">Where the defensible novelty is</H>
          <p className="text-slate-600 text-sm mb-4">These are the narrower, mechanism-level angles most worth an attorney&rsquo;s time.</p>
          <div className="space-y-3">
            {NOVELTY.map(([t, d]) => (
              <div key={t} className="flex items-start gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">{t}.</span> {d}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <H n="4">Recommended IP actions (cheapest / most certain first)</H>
            <ol className="space-y-3">
              {ACTIONS.map(([n, t, d]) => (
                <li key={n} className="flex items-start gap-3">
                  <span className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-white font-mono2 text-[11px] font-bold">{n}</span>
                  <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">{t}.</span> {d}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-8 rounded-2xl border-l-4 border-sky-500 bg-slate-50 p-5">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-sky-700">Bottom line</p>
            <p className="mt-2 text-slate-700 leading-relaxed text-sm">
              Aqualux Nexus is a strong <strong>product</strong>, but its defensible edge is the <strong>reagentless, zero-consumables,
              self-cleaning integrated system</strong> — not the &ldquo;3 strips&rdquo; or &ldquo;algae detection&rdquo; labels, most of which are already
              covered by prior art. Focus the IP spend on the specific mechanisms, and lock in the brand and design first.
            </p>
          </div>
          <Foot p="4" />
        </Page>
      </div>
    </div>
  );
}
